import { useEffect, useState, type FormEvent } from "react";
import { Eye, EyeOff, KeyRound, LoaderCircle, LockKeyhole, ShieldCheck } from "lucide-react";
import { ApiError, portalApi, type PortalApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Field } from "../components/Shared";
import { Navigate, useRouter } from "../routing/Router";
import type { Session } from "../types";

export function LoginPage() {
  const { session, state, login, error: connectionError } = useAuth();
  const { navigate } = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpRequired, setTotpRequired] = useState(false);
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (session && state === "authenticated") {
    return <Navigate to="/" replace />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password, totpRequired ? totpCode : undefined);
      navigate("/");
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.code === "totp_required") {
        setTotpRequired(true);
        setError("Enter an authenticator code or one of your recovery codes.");
        return;
      }
      setError(
        requestError instanceof ApiError && requestError.status === 401
          ? requestError.message
          : "Sign in failed. Try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-layout">
      <section className="auth-panel">
        <div className="auth-brand">
          <span className="brand-mark">
            <LockKeyhole size={18} />
          </span>
          <div>
            <strong>SwipeCut</strong>
            <span>Internal operations</span>
          </div>
        </div>
        <div className="auth-heading">
          <h1>Sign in</h1>
          <p>Use the individual account issued to you.</p>
        </div>
        <form onSubmit={submit}>
          <Field label="Email address">
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          {totpRequired ? (
            <Field
              label="Authenticator or recovery code"
              hint="Recovery codes work once."
            >
              <input
                autoComplete="one-time-code"
                autoCapitalize="characters"
                maxLength={32}
                required
                value={totpCode}
                onChange={(event) =>
                  setTotpCode(
                    event.target.value
                      .replace(/[^a-fA-F0-9\s-]/g, "")
                      .toUpperCase()
                      .slice(0, 32),
                  )
                }
              />
            </Field>
          ) : null}
          <Field label="Password">
            <div className="password-input">
              <input
                type={visible ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                className="icon-button"
                type="button"
                onClick={() => setVisible((value) => !value)}
                aria-label={visible ? "Hide password" : "Show password"}
                title={visible ? "Hide password" : "Show password"}
              >
                {visible ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </Field>
          {error || connectionError ? (
            <p className="form-alert" role="alert">
              {error ?? connectionError}
            </p>
          ) : null}
          <button className="button primary full-width" type="submit" disabled={submitting}>
            {submitting ? <LoaderCircle className="spin" size={17} /> : <KeyRound size={17} />}
            Sign in
          </button>
        </form>
      </section>
    </main>
  );
}

export function ChangePasswordPage() {
  const { session, changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!session) return <Navigate to="/login" replace />;
  if (!session.user.mustChangePassword) return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (newPassword.length < 14) {
      setError("Use at least 14 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("The new passwords do not match.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await changePassword(currentPassword, newPassword);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Password change failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-layout">
      <section className="auth-panel">
        <ShieldCheck size={28} className="auth-icon" />
        <div className="auth-heading">
          <h1>Choose a new password</h1>
          <p>This one-time step protects your individual account.</p>
        </div>
        <form onSubmit={submit}>
          <Field label="Temporary password">
            <input
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </Field>
          <Field label="New password" hint="At least 14 characters">
            <input
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Field>
          <Field label="Confirm new password">
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </Field>
          {error ? (
            <p className="form-alert" role="alert">
              {error}
            </p>
          ) : null}
          <button className="button primary full-width" type="submit" disabled={submitting}>
            {submitting ? <LoaderCircle className="spin" size={17} /> : <ShieldCheck size={17} />}
            Update password
          </button>
        </form>
      </section>
    </main>
  );
}

export function TotpSetupPage({ client = portalApi }: { client?: PortalApiClient }) {
  const { session, setSession } = useAuth();
  const [setup, setSetup] = useState<{
    secret: string;
    uri: string;
  } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [confirmedSession, setConfirmedSession] = useState<Session | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session?.user.role === "owner" && session.totpEnrollmentRequired) {
      void client.session.beginTotp().then(setSetup).catch(() => {
        setError("Authenticator setup could not start. Try again.");
      });
    }
  }, [client, session]);

  if (!session) return <Navigate to="/login" replace />;
  if (session.user.role !== "owner" || !session.totpEnrollmentRequired) {
    return <Navigate to="/" replace />;
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    try {
      const result = await client.session.confirmTotp(code);
      setRecoveryCodes(result.recoveryCodes);
      setConfirmedSession(result.session);
    } catch {
      setError("That code was not accepted. Check the time on your phone and try again.");
    }
  }

  return (
    <main className="auth-layout auth-wide">
      <section className="auth-panel">
        <ShieldCheck size={30} className="auth-icon" />
        <div className="auth-heading">
          <h1>Protect the owner account</h1>
          <p>Add this account to an authenticator app, then enter the six-digit code.</p>
        </div>
        {!setup && !error ? (
          <div className="state-inline">
            <LoaderCircle className="spin" size={18} />
            Preparing authenticator setup
          </div>
        ) : null}
        {setup ? (
          <>
            <div className="totp-setup">
              <div>
                <strong>Authenticator secret</strong>
                <p>Enter this secret manually, or open the setup link on this device.</p>
                <code>{setup.secret}</code>
                <a className="button secondary small" href={setup.uri}>
                  Open authenticator
                </a>
              </div>
            </div>
            {!recoveryCodes ? <form onSubmit={verify}>
              <Field label="Six-digit code">
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                />
              </Field>
              <button className="button primary full-width" type="submit">
                Verify and continue
              </button>
            </form> : null}
          </>
        ) : null}
        {error ? (
          <p className="form-alert" role="alert">
            {error}
          </p>
        ) : null}
        {recoveryCodes ? (
          <div className="recovery-codes">
            <strong>Recovery codes</strong>
            <p>Store these somewhere private. Each code works once.</p>
            <code>{recoveryCodes.join("\n")}</code>
            <button
              className="button primary full-width"
              type="button"
              onClick={() => {
                if (confirmedSession) setSession(confirmedSession);
              }}
            >
              I stored these codes
            </button>
          </div>
        ) : null}
      </section>
    </main>
  );
}
